/**
 * Studio's own error reporting.
 *
 * An uncaught error, an unhandled promise rejection or a React error boundary in Studio is
 * reported to stentor-api's `reportClientError` mutation, which bounds the report, groups one
 * bug's occurrences under a signature and files it in the stentor events index (STUDIO_ERROR),
 * where the same detector that watches the widgets' errors picks it up.
 *
 * Only errors from our own bundle are reported - the file or a stack frame is on this page's
 * origin. Third-party scripts (tag manager, hotjar, recaptcha, the chat widget on the sample
 * page) are not ours to watch. One report per distinct error, a handful per page load, and
 * nothing here ever throws or blocks: the POST is fire-and-forget with keepalive.
 */

export type StudioErrorKind = "error" | "unhandledrejection" | "boundary" | "console";

/** The mutation's ClientErrorInput. */
export interface StudioErrorInput {
    readonly message: string;
    readonly name?: string;
    readonly stack?: string;
    readonly kind: StudioErrorKind;
    readonly version?: string;
    readonly url?: string;
    readonly line?: number;
    readonly column?: number;
    readonly appId?: string;
    readonly sessionId?: string;
}

export const REPORT_CLIENT_ERROR_MUTATION =
    "mutation ReportClientError($error: ClientErrorInput!) { reportClientError(error: $error) }";

/** Distinct errors one page load may report; the server caps too. */
export const MAX_STUDIO_ERRORS_PER_PAGE = 5;

const MAX_MESSAGE_LENGTH = 1000;
const MAX_STACK_LENGTH = 8000;

export interface DescribeStudioErrorInput {
    readonly error: unknown;
    /** The event's message, used when `error` says nothing (cross-origin "Script error."). */
    readonly message?: string;
    readonly kind: StudioErrorKind;
    readonly version?: string;
    readonly url?: string;
    readonly line?: number;
    readonly column?: number;
    readonly appId?: string;
    readonly sessionId?: string;
}

function clip(value: unknown, max: number): string | undefined {
    if (typeof value !== "string") {
        return undefined;
    }
    const trimmed = value.trim();
    return trimmed ? trimmed.slice(0, max) : undefined;
}

function describeReason(reason: unknown): string | undefined {
    if (reason === undefined || reason === null) {
        return undefined;
    }
    if (typeof reason === "string") {
        return reason;
    }
    try {
        const json = JSON.stringify(reason);
        return json && json !== "{}" ? json : String(reason);
    } catch {
        return String(reason);
    }
}

/**
 * The report for whatever was thrown: an Error's class, message and stack, a string as the
 * message, anything else described as best it can be. Undefined when there is nothing to say.
 */
export function describeStudioError(input: DescribeStudioErrorInput): StudioErrorInput | undefined {
    const error = input.error as { name?: unknown; message?: unknown; stack?: unknown } | undefined;
    const isErrorLike = !!error && typeof error === "object" && typeof error.message === "string";

    const message =
        clip(isErrorLike ? (error as { message: string }).message : describeReason(input.error), MAX_MESSAGE_LENGTH) ||
        clip(input.message, MAX_MESSAGE_LENGTH);
    if (!message) {
        return undefined;
    }

    const report: { -readonly [K in keyof StudioErrorInput]: StudioErrorInput[K] } = {
        message,
        name: isErrorLike ? clip(error.name, 120) : undefined,
        stack: isErrorLike ? clip(error.stack, MAX_STACK_LENGTH) : undefined,
        kind: input.kind,
        version: clip(input.version, 120),
        url: clip(input.url, 2048),
        line: typeof input.line === "number" && input.line > 0 ? input.line : undefined,
        column: typeof input.column === "number" && input.column > 0 ? input.column : undefined,
        appId: clip(input.appId, 120),
        sessionId: clip(input.sessionId, 64),
    };
    (Object.keys(report) as Array<keyof StudioErrorInput>).forEach((key) => {
        if (report[key] === undefined) {
            delete report[key];
        }
    });
    return report;
}

/**
 * Whether an error is Studio's own: its file, or a frame of its stack, is on this page's origin.
 * Nothing matches an error with no location at all (the cross-origin "Script error.").
 */
export function isOwnError(location: { readonly file?: string; readonly stack?: string }, origin: string): boolean {
    if (!origin) {
        return false;
    }
    if (location.file && location.file.indexOf(origin) === 0) {
        return true;
    }
    return !!location.stack && location.stack.indexOf(origin) >= 0;
}

function randomSessionId(): string {
    try {
        const bytes = new Uint8Array(8);
        crypto.getRandomValues(bytes);
        return `studio_${Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("")}`;
    } catch {
        return `studio_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`;
    }
}

export interface StudioErrorReporterOptions {
    /** The Studio API (config.apiUrl); the mutation is POSTed to `${api}/graphql`. */
    readonly api: string;
    /** The user's access token, read at send time; an error on the login page has none. */
    readonly getToken: () => string | undefined;
    /** The app the page is on, read at send time. */
    readonly getAppId?: () => string | undefined;
    /** The Studio build (__RELEASE__). */
    readonly version?: string;
    readonly maxPerPage?: number;
}

export interface StudioErrorReporter {
    /** Reports an error the page caught itself (a boundary). Not filtered by origin: the page vouches for it. */
    report(error: unknown, kind?: StudioErrorKind): boolean;
    /** Removes the window listeners and forgets the reporter. */
    uninstall(): void;
}

let installed: StudioErrorReporter | undefined;

/**
 * Reports through the page's installed reporter, for code (the error boundary) that has no
 * handle on it. False when nothing is installed or the report was dropped. Never throws.
 */
export function reportStudioError(error: unknown, kind: StudioErrorKind = "boundary"): boolean {
    try {
        return installed ? installed.report(error, kind) : false;
    } catch {
        return false;
    }
}

function post(options: StudioErrorReporterOptions, report: StudioErrorInput): boolean {
    if (typeof fetch !== "function") {
        return false;
    }
    try {
        const headers: Record<string, string> = { "content-type": "application/json" };
        const token = options.getToken();
        if (token) {
            headers["x-xapp-usertoken"] = token;
        }
        fetch(`${options.api}/graphql`, {
            method: "POST",
            headers,
            body: JSON.stringify({ query: REPORT_CLIENT_ERROR_MUTATION, variables: { error: report } }),
            keepalive: true,
        }).catch(() => undefined);
        return true;
    } catch {
        return false;
    }
}

/**
 * Installs the page's error reporter: window `error` and `unhandledrejection` listeners filtered
 * to our own origin, one report per distinct error, at most `maxPerPage` per page load. A second
 * install replaces the first. Never throws.
 */
export function installStudioErrorReporting(options: StudioErrorReporterOptions): StudioErrorReporter {
    const noop: StudioErrorReporter = { report: () => false, uninstall: () => undefined };
    try {
        if (typeof window === "undefined") {
            return noop;
        }
        if (installed) {
            installed.uninstall();
        }

        const maxPerPage = options.maxPerPage ?? MAX_STUDIO_ERRORS_PER_PAGE;
        const sessionId = randomSessionId();
        const seen: Record<string, true> = {};
        let sent = 0;

        const origin = (() => {
            try {
                return window.location.origin;
            } catch {
                return "";
            }
        })();

        const describe = (
            error: unknown,
            kind: StudioErrorKind,
            location: { message?: string; line?: number; column?: number } = {}
        ): StudioErrorInput | undefined => {
            let appId: string | undefined;
            try {
                appId = options.getAppId ? options.getAppId() : undefined;
            } catch {
                appId = undefined;
            }
            let url: string | undefined;
            try {
                url = window.location.href;
            } catch {
                url = undefined;
            }
            return describeStudioError({
                error,
                message: location.message,
                kind,
                version: options.version,
                url,
                line: location.line,
                column: location.column,
                appId,
                sessionId,
            });
        };

        const send = (report: StudioErrorInput | undefined): boolean => {
            if (!report || sent >= maxPerPage) {
                return false;
            }
            const key = `${report.name || ""}|${report.message.slice(0, 200)}`;
            if (seen[key]) {
                return false;
            }
            seen[key] = true;
            const queued = post(options, report);
            if (queued) {
                sent++;
            }
            return queued;
        };

        const onError = (event: ErrorEvent): void => {
            try {
                const stack = (event.error as { stack?: unknown } | undefined)?.stack;
                if (
                    !isOwnError({ file: event.filename, stack: typeof stack === "string" ? stack : undefined }, origin)
                ) {
                    return;
                }
                send(
                    describe(event.error, "error", { message: event.message, line: event.lineno, column: event.colno })
                );
            } catch {
                // Reporting must never break the page.
            }
        };

        const onRejection = (event: Event): void => {
            try {
                const reason = (event as { reason?: unknown }).reason;
                const stack = (reason as { stack?: unknown } | undefined)?.stack;
                if (!isOwnError({ stack: typeof stack === "string" ? stack : undefined }, origin)) {
                    return;
                }
                send(describe(reason, "unhandledrejection"));
            } catch {
                // Reporting must never break the page.
            }
        };

        window.addEventListener("error", onError);
        window.addEventListener("unhandledrejection", onRejection);

        const reporter: StudioErrorReporter = {
            report: (error, kind = "boundary") => {
                try {
                    return send(describe(error, kind));
                } catch {
                    return false;
                }
            },
            uninstall: () => {
                try {
                    window.removeEventListener("error", onError);
                    window.removeEventListener("unhandledrejection", onRejection);
                } catch {
                    // Nothing left to undo.
                }
                if (installed === reporter) {
                    installed = undefined;
                }
            },
        };
        installed = reporter;
        return reporter;
    } catch {
        return noop;
    }
}
